import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  collection,
  doc,
  addDoc,
  setDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import {
  MEASUREMENT_RULES,
  MEASUREMENT_ITEM_BY_KEY,
  CUSTOM_ITEM_KEY,
  UNITS,
  unitLabel,
  formatQty,
  ruleFields,
  itemsForSection,
  suggestedItemsForSection,
  blankMeasurementLine,
  computeMeasurement,
  sectionLength,
  summariseMeasurements,
} from "./measurementModel";
import {
  ACTIVITY_LABELS,
  formatChainage,
  excavationConditionMeta,
  clearanceSubTypeMeta,
} from "./VisualProgressTracker";

/**
 * MeasurementSheet — the daily measurements table for one recorded section.
 *
 * A section is a progressSegments doc: an activity over a chainage range, with
 * the properties captured against it (wet or dry material, top soil or premix
 * clearance). That context is what makes the sheet useful — open the sheet on
 * a wet excavation run and the dewatering and wet-excavation items are already
 * at the top of the picker.
 *
 * Each line picks an item, inherits the item's rule and defaults, and the
 * quantity follows from the section length and the properties entered. Lines
 * are written to projects/{id}/progressMeasurements so the period summary in
 * Productivity can roll them up across every section.
 *
 * Props
 * -----
 *   projectId — the project the section belongs to
 *   asset     — the progressAssets doc (supplies trench width/depth defaults)
 *   segment   — the progressSegments doc being measured
 *   onClose   — close handler
 */
export default function MeasurementSheet({ projectId, asset, segment, onClose }) {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const segLength = sectionLength(segment);
  const defaultDate = segment?.diaryDate || new Date().toISOString().slice(0, 10);

  /* ----- Live measurement lines for this section ----- */

  useEffect(() => {
    if (!projectId || !segment?.id) return;
    const q = query(
      collection(db, "projects", projectId, "progressMeasurements"),
      where("segmentId", "==", segment.id)
    );
    const unsub = onSnapshot(
      q,
      (snap) => setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => setError(err?.message || "Could not load measurements.")
    );
    return () => unsub();
  }, [projectId, segment?.id]);

  const sorted = useMemo(
    () =>
      [...rows].sort((a, b) =>
        String(a.date || "").localeCompare(String(b.date || "")) ||
        String(a.itemLabel || "").localeCompare(String(b.itemLabel || ""))
      ),
    [rows]
  );

  const summary = useMemo(() => summariseMeasurements(rows), [rows]);
  const suggested = useMemo(() => suggestedItemsForSection(segment), [segment]);
  const pickerItems = useMemo(() => itemsForSection(segment), [segment]);

  /* ----- ESC to close ----- */
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !editingId && !draft) onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, editingId, draft]);

  /* ----- Draft handling ----- */

  function startNew(itemKey) {
    setEditingId(null);
    setDraft({
      ...blankMeasurementLine(itemKey, { segment, asset }),
      date: defaultDate,
    });
  }

  function startEdit(row) {
    setEditingId(row.id);
    setDraft({
      itemKey: row.itemKey || CUSTOM_ITEM_KEY,
      itemLabel: row.itemLabel || "",
      unit: row.unit || "m3",
      rule: row.rule || "count",
      widthM: row.widthM ?? "",
      thicknessMm: row.thicknessMm ?? "",
      layers: row.layers ?? 1,
      densityTPerM3: row.densityTPerM3 ?? "",
      countQty: row.countQty ?? "",
      lengthOverrideM: row.lengthOverrideM ?? "",
      notes: row.notes || "",
      date: row.date || defaultDate,
    });
  }

  function cancelDraft() {
    setDraft(null);
    setEditingId(null);
    setError("");
  }

  const setField = (field, value) =>
    setDraft((d) => {
      const next = { ...d, [field]: value };
      // Switching item pulls in that item's rule, unit and defaults, but keeps
      // the date and any length override the user already set.
      if (field === "itemKey") {
        const seeded = blankMeasurementLine(value, { segment, asset });
        return { ...seeded, date: d.date, lengthOverrideM: d.lengthOverrideM, notes: d.notes };
      }
      if (field === "rule") {
        next.unit = MEASUREMENT_RULES[value]?.defaultUnit || d.unit;
      }
      return next;
    });

  const preview = draft ? computeMeasurement(draft, segment) : null;
  const fields = draft ? ruleFields(draft.rule) : [];

  async function saveDraft() {
    if (!draft || !projectId || !segment?.id) return;
    const label = (draft.itemLabel || MEASUREMENT_ITEM_BY_KEY[draft.itemKey]?.label || "").trim();
    if (!label) {
      setError("Give the line an item description.");
      return;
    }
    const computed = computeMeasurement(draft, segment);
    if (!(computed.qty > 0)) {
      setError("The properties entered give a quantity of zero — check the width, thickness or quantity.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const payload = {
        projectId,
        assetId: segment.assetId || asset?.id || "",
        assetName: asset?.assetName || segment.assetName || "",
        segmentId: segment.id,
        segmentActivity: segment.activity || "",
        segmentStatus: segment.status || "",
        // Properties carried from the section — these are what make a
        // measurement defensible six months later.
        excavationCondition: segment.excavationCondition || "",
        clearanceSubType: segment.clearanceSubType || "",
        chainageStart: Number(segment.chainageStart) || 0,
        chainageEnd: Number(segment.chainageEnd) || 0,
        segmentLengthM: segLength,
        diaryNo: segment.diaryNo || "",
        diaryId: segment.diaryId || "",
        date: draft.date || defaultDate,

        itemKey: draft.itemKey || CUSTOM_ITEM_KEY,
        itemLabel: label,
        unit: computed.unit,
        rule: draft.rule,
        widthM: numOrNull(draft.widthM),
        thicknessMm: numOrNull(draft.thicknessMm),
        layers: numOrNull(draft.layers),
        densityTPerM3: numOrNull(draft.densityTPerM3),
        countQty: numOrNull(draft.countQty),
        lengthOverrideM: numOrNull(draft.lengthOverrideM),
        measuredLengthM: computed.length,
        qty: computed.qty,
        workings: computed.workings,
        notes: draft.notes || "",
        updatedAt: serverTimestamp(),
      };

      if (editingId) {
        await setDoc(doc(db, "projects", projectId, "progressMeasurements", editingId), payload, { merge: true });
      } else {
        await addDoc(collection(db, "projects", projectId, "progressMeasurements"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }
      cancelDraft();
    } catch (err) {
      setError(err?.message || "Could not save the measurement.");
    } finally {
      setSaving(false);
    }
  }

  async function removeRow(row) {
    if (!window.confirm(`Delete the ${row.itemLabel} line of ${formatQty(row.qty, row.unit)} ${unitLabel(row.unit)}?`)) return;
    try {
      await deleteDoc(doc(db, "projects", projectId, "progressMeasurements", row.id));
    } catch (err) {
      setError(err?.message || "Could not delete the measurement.");
    }
  }

  if (!segment) return null;

  const activity = ACTIVITY_LABELS[segment.activity] || segment.activity;
  const wetDry = segment.activity === "excavation" ? excavationConditionMeta(segment.excavationCondition) : null;
  const clearSub = segment.activity === "site_clearance" ? clearanceSubTypeMeta(segment.clearanceSubType) : null;

  return createPortal(
    <div style={st.overlay} onClick={onClose}>
      <div style={st.dialog} onClick={(e) => e.stopPropagation()}>

        {/* ── Header: the section being measured ── */}
        <div style={st.head}>
          <div>
            <div style={st.eyebrow}>Daily measurements</div>
            <h3 style={st.title}>
              {activity} · {formatChainage(segment.chainageStart)} → {formatChainage(segment.chainageEnd)}
            </h3>
            <div style={st.propRow}>
              <span style={st.prop}><b>Section length</b> {segLength.toLocaleString("en-ZA")} m</span>
              {wetDry && wetDry.key && (
                <span style={{ ...st.prop, color: wetDry.colour, background: wetDry.soft, borderColor: wetDry.colour }}>
                  {wetDry.icon} {wetDry.label}
                </span>
              )}
              {clearSub && clearSub.key && (
                <span style={{ ...st.prop, color: clearSub.colour, background: clearSub.soft, borderColor: clearSub.colour }}>
                  {clearSub.prefix} {clearSub.label}
                </span>
              )}
              {segment.diaryNo && <span style={st.prop}><b>Diary</b> #{segment.diaryNo}</span>}
              {segment.diaryDate && <span style={st.prop}><b>Date</b> {segment.diaryDate}</span>}
            </div>
          </div>
          <button style={st.closeX} onClick={onClose} title="Close (ESC)">×</button>
        </div>

        <div style={st.body}>
          {error && <div style={st.error}>{error}</div>}

          {/* ── Quick-add chips for the items this section usually carries ── */}
          {suggested.length > 0 && !draft && (
            <div style={st.suggestBar}>
              <span style={st.suggestLabel}>Measure</span>
              {suggested.map((item) => (
                <button key={item.key} style={st.suggestChip} onClick={() => startNew(item.key)}>
                  + {item.label} <em style={st.suggestUnit}>{unitLabel(item.unit)}</em>
                </button>
              ))}
              <button style={st.suggestChipMuted} onClick={() => startNew(CUSTOM_ITEM_KEY)}>
                + Other item…
              </button>
            </div>
          )}
          {suggested.length === 0 && !draft && (
            <div style={st.suggestBar}>
              <span style={st.suggestLabel}>Measure</span>
              <button style={st.suggestChip} onClick={() => startNew(pickerItems[0]?.key || CUSTOM_ITEM_KEY)}>
                + Add measurement line
              </button>
            </div>
          )}

          {/* ── The line editor ── */}
          {draft && (
            <div style={st.editor}>
              <div style={st.editorHead}>
                <strong style={st.editorTitle}>{editingId ? "Edit line" : "New measurement line"}</strong>
                <span style={st.editorRule}>{MEASUREMENT_RULES[draft.rule]?.label}</span>
              </div>

              <div style={st.editorGrid}>
                <label style={st.field}>
                  Item
                  <select style={st.input} value={draft.itemKey} onChange={(e) => setField("itemKey", e.target.value)}>
                    <option value={CUSTOM_ITEM_KEY}>— custom item —</option>
                    {pickerItems.map((item) => (
                      <option key={item.key} value={item.key}>
                        {item.group} · {item.label} ({unitLabel(item.unit)})
                      </option>
                    ))}
                  </select>
                </label>

                <label style={st.field}>
                  Description
                  <input
                    style={st.input}
                    value={draft.itemLabel}
                    onChange={(e) => setField("itemLabel", e.target.value)}
                    placeholder="e.g. 100 to 250mm stone"
                  />
                </label>

                <label style={st.field}>
                  Date measured
                  <input style={st.input} type="date" value={draft.date || ""} onChange={(e) => setField("date", e.target.value)} />
                </label>

                <label style={st.field}>
                  Calculation
                  <select style={st.input} value={draft.rule} onChange={(e) => setField("rule", e.target.value)}>
                    {Object.values(MEASUREMENT_RULES).map((r) => (
                      <option key={r.key} value={r.key}>{r.label}</option>
                    ))}
                  </select>
                </label>

                <label style={st.field}>
                  Unit
                  <select style={st.input} value={draft.unit} onChange={(e) => setField("unit", e.target.value)}>
                    {Object.values(UNITS).map((u) => (
                      <option key={u.key} value={u.key}>{u.label}</option>
                    ))}
                  </select>
                </label>

                <label style={st.field}>
                  Length measured (m)
                  <input
                    style={st.input}
                    type="number"
                    value={draft.lengthOverrideM}
                    onChange={(e) => setField("lengthOverrideM", e.target.value)}
                    placeholder={`whole section — ${segLength} m`}
                  />
                  <span style={st.helper}>Leave blank to measure the whole section.</span>
                </label>

                {fields.includes("widthM") && (
                  <label style={st.field}>
                    Width (m)
                    <input style={st.input} type="number" step="any" value={draft.widthM} onChange={(e) => setField("widthM", e.target.value)} />
                    {MEASUREMENT_ITEM_BY_KEY[draft.itemKey]?.hint && (
                      <span style={st.helper}>{MEASUREMENT_ITEM_BY_KEY[draft.itemKey].hint}</span>
                    )}
                  </label>
                )}

                {fields.includes("thicknessMm") && (
                  <label style={st.field}>
                    Thickness (mm)
                    <input style={st.input} type="number" step="any" value={draft.thicknessMm} onChange={(e) => setField("thicknessMm", e.target.value)} />
                  </label>
                )}

                {fields.includes("layers") && (
                  <label style={st.field}>
                    Layers / runs
                    <input style={st.input} type="number" step="any" value={draft.layers} onChange={(e) => setField("layers", e.target.value)} />
                  </label>
                )}

                {fields.includes("densityTPerM3") && (
                  <label style={st.field}>
                    Density (t/m³)
                    <input style={st.input} type="number" step="any" value={draft.densityTPerM3} onChange={(e) => setField("densityTPerM3", e.target.value)} />
                  </label>
                )}

                {fields.includes("countQty") && (
                  <label style={st.field}>
                    Quantity ({unitLabel(draft.unit)})
                    <input style={st.input} type="number" step="any" value={draft.countQty} onChange={(e) => setField("countQty", e.target.value)} />
                  </label>
                )}

                <label style={{ ...st.field, gridColumn: "1 / -1" }}>
                  Notes
                  <input style={st.input} value={draft.notes} onChange={(e) => setField("notes", e.target.value)} placeholder="Optional — e.g. extra rock encountered at CH 462" />
                </label>
              </div>

              {/* Live workings so the site team can check the sum */}
              <div style={st.previewBar}>
                <div>
                  <span style={st.previewLabel}>Calculated quantity</span>
                  <strong style={st.previewQty}>
                    {formatQty(preview.qty, preview.unit)} {unitLabel(preview.unit)}
                  </strong>
                </div>
                <code style={st.previewWorkings}>
                  {preview.workings}
                  {preview.usedOverride ? " (part section)" : ""}
                </code>
                <div style={st.editorActions}>
                  <button style={st.ghostBtn} onClick={cancelDraft} disabled={saving}>Cancel</button>
                  <button style={st.primaryBtn} onClick={saveDraft} disabled={saving}>
                    {saving ? "Saving…" : editingId ? "Update line" : "Add line"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Recorded lines ── */}
          {sorted.length === 0 ? (
            <div style={st.empty}>
              No measurements recorded against this section yet.
            </div>
          ) : (
            <div style={st.tableWrap}>
              <table style={st.table}>
                <thead>
                  <tr>
                    <th style={st.th}>Date</th>
                    <th style={st.th}>Item</th>
                    <th style={{ ...st.th, textAlign: "right" }}>Length</th>
                    <th style={st.th}>Properties</th>
                    <th style={{ ...st.th, textAlign: "right" }}>Quantity</th>
                    <th style={{ ...st.th, textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((row) => (
                    <tr key={row.id}>
                      <td style={st.td}>{row.date || "—"}</td>
                      <td style={st.td}>
                        <strong>{row.itemLabel}</strong>
                        {row.notes && <div style={st.tdNote}>{row.notes}</div>}
                      </td>
                      <td style={{ ...st.td, textAlign: "right", fontFamily: MONO }}>
                        {Number(row.measuredLengthM || 0).toLocaleString("en-ZA")} m
                      </td>
                      <td style={{ ...st.td, fontFamily: MONO, fontSize: 11, color: "#64748b" }}>
                        {describeProperties(row)}
                      </td>
                      <td style={{ ...st.td, textAlign: "right", fontWeight: 800, fontFamily: MONO }}>
                        {formatQty(row.qty, row.unit)} {unitLabel(row.unit)}
                      </td>
                      <td style={{ ...st.td, textAlign: "right" }}>
                        <button style={st.miniBtn} onClick={() => startEdit(row)}>Edit</button>
                        <button style={st.miniBtnDanger} onClick={() => removeRow(row)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Section totals ── */}
          {summary.items.length > 0 && (
            <div style={st.totals}>
              <span style={st.totalsTitle}>Section total</span>
              {summary.items.map((item) => (
                <span key={item.key} style={st.totalChip}>
                  {item.label}
                  <strong style={st.totalChipQty}>
                    {formatQty(item.total, item.unit)} {unitLabel(item.unit)}
                  </strong>
                </span>
              ))}
            </div>
          )}
        </div>

        <div style={st.foot}>
          <span style={st.footHint}>
            Lines recorded here roll up into the period measurement sheet under Productivity.
          </span>
          <button style={st.ghostBtn} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

const MONO = "ui-monospace, SFMono-Regular, monospace";

function numOrNull(v) {
  const n = Number(v);
  return v === "" || v === null || v === undefined || !Number.isFinite(n) ? null : n;
}

/** The properties that produced a line's quantity, in one short string. */
export function describeProperties(row) {
  const bits = [];
  if (row.widthM) bits.push(`W ${row.widthM} m`);
  if (row.thicknessMm) bits.push(`T ${row.thicknessMm} mm`);
  if (row.layers && Number(row.layers) !== 1) bits.push(`× ${row.layers}`);
  if (row.densityTPerM3) bits.push(`${row.densityTPerM3} t/m³`);
  if (!bits.length && row.countQty != null) bits.push("entered directly");
  return bits.join(" · ") || "—";
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const st = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: 20, zIndex: 2200,
  },
  dialog: {
    background: "#ffffff", borderRadius: 18, width: "min(1080px, 100%)",
    maxHeight: "92vh", display: "flex", flexDirection: "column",
    boxShadow: "0 30px 80px rgba(15,23,42,0.4)", overflow: "hidden",
    fontFamily: "ui-sans-serif, system-ui, sans-serif",
  },
  head: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    gap: 16, padding: "16px 20px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc",
  },
  eyebrow: { fontSize: 10, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase", color: "#64748b" },
  title: { margin: "3px 0 8px", fontSize: 17, fontWeight: 800, color: "#0f172a" },
  propRow: { display: "flex", flexWrap: "wrap", gap: 8 },
  prop: {
    fontSize: 11, fontWeight: 700, color: "#334155", background: "#ffffff",
    border: "1px solid #e2e8f0", borderRadius: 999, padding: "4px 10px",
  },
  closeX: {
    border: "none", background: "transparent", fontSize: 26, lineHeight: 1,
    color: "#64748b", cursor: "pointer", padding: 0,
  },

  body: { padding: 18, overflow: "auto", display: "grid", gap: 14 },
  error: {
    background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c",
    borderRadius: 10, padding: "9px 12px", fontSize: 12, fontWeight: 700,
  },

  suggestBar: { display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" },
  suggestLabel: { fontSize: 10, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase", color: "#64748b", marginRight: 2 },
  suggestChip: {
    fontSize: 12, fontWeight: 700, color: "#0f172a", background: "#ffffff",
    border: "1px solid #cbd5e1", borderRadius: 999, padding: "6px 13px", cursor: "pointer",
  },
  suggestChipMuted: {
    fontSize: 12, fontWeight: 700, color: "#64748b", background: "#f8fafc",
    border: "1px dashed #cbd5e1", borderRadius: 999, padding: "6px 13px", cursor: "pointer",
  },
  suggestUnit: { color: "#94a3b8", fontStyle: "normal", fontWeight: 700, marginLeft: 3 },

  editor: { border: "1px solid #bfdbfe", background: "#f8fbff", borderRadius: 14, padding: 14, display: "grid", gap: 12 },
  editorHead: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" },
  editorTitle: { fontSize: 13, fontWeight: 800, color: "#0f172a" },
  editorRule: { fontSize: 11, fontWeight: 700, color: "#1d4ed8", background: "#dbeafe", borderRadius: 999, padding: "4px 10px" },
  editorGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 10 },
  field: { display: "grid", gap: 5, fontSize: 11, fontWeight: 800, color: "#475569", textTransform: "uppercase", letterSpacing: 0.4 },
  input: {
    border: "1px solid #cbd5e1", borderRadius: 9, padding: "8px 10px",
    fontSize: 13, fontWeight: 600, color: "#0f172a", background: "#ffffff",
    textTransform: "none", letterSpacing: 0, fontFamily: "inherit",
  },
  helper: { fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "none", letterSpacing: 0 },

  previewBar: {
    display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
    background: "#0f172a", borderRadius: 12, padding: "10px 14px",
  },
  previewLabel: { display: "block", fontSize: 10, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase", color: "#94a3b8" },
  previewQty: { fontSize: 20, fontWeight: 800, color: "#f8fafc" },
  previewWorkings: { fontSize: 12, color: "#7dd3fc", fontFamily: MONO },
  editorActions: { marginLeft: "auto", display: "flex", gap: 8 },

  primaryBtn: {
    border: "none", borderRadius: 10, padding: "8px 16px", background: "#2563eb",
    color: "#ffffff", fontWeight: 800, fontSize: 12, cursor: "pointer",
  },
  ghostBtn: {
    border: "1px solid #cbd5e1", borderRadius: 10, padding: "8px 16px",
    background: "#ffffff", color: "#0f172a", fontWeight: 700, fontSize: 12, cursor: "pointer",
  },

  empty: { padding: 20, borderRadius: 12, background: "#f8fafc", color: "#64748b", fontSize: 13, textAlign: "center" },

  tableWrap: { border: "1px solid #e2e8f0", borderRadius: 12, overflow: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 720 },
  th: {
    textAlign: "left", padding: "9px 12px", background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0", color: "#64748b",
    fontSize: 10, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase",
  },
  td: { padding: "9px 12px", borderBottom: "1px solid #f1f5f9", color: "#0f172a", verticalAlign: "top" },
  tdNote: { fontSize: 11, color: "#94a3b8", fontWeight: 600, marginTop: 2 },
  miniBtn: {
    border: "1px solid #cbd5e1", borderRadius: 8, background: "#ffffff",
    padding: "4px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", marginLeft: 6, color: "#0f172a",
  },
  miniBtnDanger: {
    border: "1px solid #fecaca", borderRadius: 8, background: "#fff5f5",
    padding: "4px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", marginLeft: 6, color: "#b91c1c",
  },

  totals: { display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" },
  totalsTitle: { fontSize: 10, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase", color: "#64748b" },
  totalChip: {
    display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 700,
    color: "#334155", background: "#f1f5f9", border: "1px solid #e2e8f0",
    borderRadius: 999, padding: "5px 12px",
  },
  totalChipQty: { fontFamily: MONO, color: "#0f172a" },

  foot: {
    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
    padding: "12px 20px", borderTop: "1px solid #e2e8f0", background: "#f8fafc",
  },
  footHint: { fontSize: 11, color: "#64748b", fontWeight: 600 },
};
